<template>
  <div class="position-recommendation-modal">

    <!-- Progress state (only show when no scored items yet) -->
    <div v-if="isActive && scoredItems.length === 0" class="progress-section">
      <div class="steps">
        <div v-for="(step, idx) in steps" :key="idx" class="step" :class="{ active: currentStepIndex >= idx, current: currentStepIndex === idx }">
          <span class="step-dot">{{ currentStepIndex > idx ? '✓' : (idx + 1) }}</span>
          <span class="step-label">{{ step.label }}</span>
        </div>
      </div>

      <div v-if="lastRole || matchedJobTitles.length" class="matched-titles-block">
        <h4 v-if="lastRole">תפקיד אחרון: <strong>{{ lastRole }}</strong></h4>
        <h4>ג'וב טייטלים שזוהו</h4>
        <div class="title-tags">
          <span v-for="(title, idx) in matchedJobTitles" :key="idx" class="title-tag">{{ title }}</span>
        </div>
      </div>

      <div v-if="session && session.progress && session.progress.total > 0" class="progress-bar-container">
        <p class="filtered-count">נמצאו <strong>{{ session.progress.total }}</strong> משרות שעברו סינון</p>
        <progress class="progress is-info" :value="session.progress.scored" :max="session.progress.total"></progress>
        <p class="progress-text">{{ session.progress.scored }}/{{ session.progress.total }} ניקוד חושב</p>
      </div>

      <div class="loading-spinner-container">
        <div class="loading-spinner"></div>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="session && session.status === 'failed'" class="error-section">
      <p class="error-text">{{ errorMessage }}</p>
      <button @click="retry" class="button is-warning">נסה שוב</button>
    </div>

    <!-- Results state (scored / evaluating / ready) -->
    <div v-else-if="session && session.status === 'scored'" class="results-section">
      <div v-if="lastRole || matchedJobTitles.length" class="matched-titles-block">
        <h4 v-if="lastRole">תפקיד אחרון: <strong>{{ lastRole }}</strong></h4>
        <h4>ג'וב טייטלים שזוהו</h4>
        <div class="title-tags">
          <span v-for="(title, idx) in matchedJobTitles" :key="idx" class="title-tag">{{ title }}</span>
        </div>
      </div>

      <div v-if="session.booster_reasoning" class="reasoning-block">
        <h4>ניתוח AI</h4>
        <p class="reasoning-text">{{ session.booster_reasoning }}</p>
      </div>

      <div class="results-summary">
        <span>{{ session.progress.total }} משרות עברו סינון</span>
        <span> | {{ session.progress.scored || session.progress.total }} קיבלו ניקוד</span>
        <span> | {{ session.total_ranked || 0 }} ייחודיות (אחרי סינון כפילויות חברה)</span>
      </div>

      <!-- Loading offers -->
      <div v-if="isLoadingOffers" class="is-flex is-align-items-center is-justify-content-center" style="padding: 20px;">
        <div class="loading"></div>
      </div>

      <!-- Scored items list (all scored offers sorted by score descending) -->
      <div v-if="scoredItems.length > 0" class="scored-section">
        <div class="scored-divider">
          <span class="scored-divider-label">כל המשרות לפי ניקוד ({{ scoredItems.length }})</span>
        </div>

        <div
          v-for="(item, idx) in scoredItems"
          :key="'scored-' + item.offer_id"
          style="border-bottom: 1px solid rgba(0, 0, 0, 0.12); padding-top: 10px; padding-bottom: 10px;"
        >
          <div class="scored-item-banner">
            <span class="scored-item-rank">#{{ idx + 1 }}</span>
            <span class="scored-item-score">ניקוד: {{ item.score || 0 }}</span>
            <span class="scored-item-offer-id">ID: {{ item.offer_id }}</span>
            <span v-if="aiResults[item.offer_id]" class="ai-reco-badge" :class="decisionBadgeClass(aiResults[item.offer_id].ba_decision)" style="font-size: 0.7rem;">
              {{ decisionLabel(aiResults[item.offer_id].ba_decision) }}
            </span>
            <button
              v-if="!aiResults[item.offer_id]"
              @click="evaluateOffer(item.offer_id)"
              class="button is-small"
              style="background: #7c5cbf; color: white; font-size: 0.75rem;"
              :disabled="isOfferLoading(item.offer_id)"
            >
              {{ isOfferLoading(item.offer_id) ? 'מחשב...' : 'חשב עם AI' }}
            </button>
            <a
              v-if="offersMap[item.offer_id]"
              @click="sendCv(offersMap[item.offer_id])"
              class="button is-small is-info"
              style="font-size: 0.75rem;"
            >Send CV</a>
            <offer-status
              v-if="offersMap[item.offer_id]"
              class="scored-item-status"
              :payload="offersMap[item.offer_id]"
              buttonClass="button is-small is-rounded"
            ></offer-status>
            <span class="scored-item-company">{{ item.company_name }}</span>
            <link-entity
              v-if="offersMap[item.offer_id] && offersMap[item.offer_id].position"
              class="scored-item-position-link"
              :additional_data="{ resource_type: 'Position' }"
              :payload="offersMap[item.offer_id].position"
            ></link-entity>
            <span v-else class="scored-item-position-text">{{ item.position_title }}</span>
          </div>

          <!-- AI result inline -->
          <div v-if="aiResults[item.offer_id] && aiResults[item.offer_id].ba_reasoning" class="ai-reco-reasoning" dir="rtl">
            <b>סיכום AI: </b><span v-html="sanitize(aiResults[item.offer_id].ba_reasoning)"></span>
          </div>
          <div v-if="aiResults[item.offer_id] && aiResults[item.offer_id].ba_proposer" class="ai-reco-proposer" dir="rtl">
            <b>הצעה: </b><span v-html="sanitize(aiResults[item.offer_id].ba_proposer)"></span>
          </div>

          <!-- Loading indicator for this offer -->
          <div v-if="isOfferLoading(item.offer_id)" class="offer-ai-loading">
            <div class="loading-spinner" style="width: 20px; height: 20px; border-width: 3px;"></div>
            <span>מחשב הערכת AI...</span>
          </div>

          <template v-if="offersMap[item.offer_id]">
            <offer-card :offer="offersMap[item.offer_id]" :item="item" :vm="thisVm"></offer-card>
          </template>
          <template v-else>
            <div class="is-flex is-align-items-center mb-4" style="margin-top: 8px;">
              <p><b>Position:</b> {{ item.position_title }}</p>
              <p style="margin-right: 10px;"><b>Company:</b> {{ item.company_name }}</p>
            </div>
          </template>
        </div>
      </div>

      <div v-if="scoredItems.length === 0 && !isLoadingOffers" class="no-results">
        <p>לא נמצאו תוצאות להצגה</p>
      </div>
    </div>

    <!-- Initial / no data -->
    <div v-else class="loading-section">
      <div class="loading-spinner"></div>
      <p>טוען...</p>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import LinkEntity from '@/components/LinkEntity'
import OfferStatus from '@/components/OfferStatus'
import WhatsAppLink from '@/components/WhatsAppLink'
import Message from '@/components/Message'
import AttributeDisplay from '@/components/AttributeDisplay'
import TimeHelper from '@/custom/classes/time_helper.js'
import OffersHelper from '@/custom/classes/offers_helper.js'

const OfferCard = {
  name: 'OfferCard',
  components: { LinkEntity, OfferStatus, WhatsAppLink, Message, AttributeDisplay },
  props: ['offer', 'item', 'vm'],
  data () {
    return {
      time_helper: new TimeHelper(),
      offers_helper: new OffersHelper(),
      copiedOfferId: null
    }
  },
  methods: {
    copyOfferId (offerId) {
      navigator.clipboard.writeText(offerId.toString()).then(() => {
        this.copiedOfferId = offerId
        setTimeout(() => { this.copiedOfferId = null }, 2000)
      })
    },
    sendCv () {
      this.vm.sendCv(this.offer)
    },
    vested () {
      this.vm.vested(this.offer)
    },
    rejectOffer () {
      this.vm.rejectOffer(this.offer)
    },
    openWhatsappTemplatesModal () {
      this.vm.openWhatsappTemplatesModal(this.offer)
    },
    sendWhatsapp () {
      this.vm.sendWhatsapp(this.offer)
    }
  },
  template: `
    <div>
      <div class="is-flex is-align-items-center mb-4" style="margin-top: 8px;">
        <div class="is-flex is-align-items-center">
          <p><b>Position:</b></p>
          <link-entity class="mx-2" :additional_data="{ resource_type: 'Position' }" :payload="offer.position"></link-entity>
        </div>
        <div class="is-flex is-align-items-center my-5">
          <p><b>Company:</b></p>
          <link-entity class="mx-2" :additional_data="{ resource_type: 'Company' }" :payload="offer.position.company"></link-entity>
        </div>
      </div>
      <div class="is-flex is-align-items-center">
        <div class="is-flex is-align-items-center">
          <p><b>Name: </b></p>
          <link-entity style="margin-right: 6px;" :payload="offer.applicant"></link-entity>
        </div>
        <div style="margin-right: 10px;" class="is-flex is-align-items-center">
          <div class="is-flex is-align-items-center" style="margin-right: 10px;">
            <span style="margin-right: 5px;"><b>ID:</b> {{ item.offer_id }}</span>
            <button @click="copyOfferId(item.offer_id)" class="button is-small is-light" :class="{ 'is-success': copiedOfferId === item.offer_id }" style="margin-right: 5px;" :title="copiedOfferId === item.offer_id ? 'Copied!' : 'Copy Offer ID'">
              <span v-if="copiedOfferId === item.offer_id">✓</span>
              <span v-else>📋</span>
            </button>
          </div>
          <link-entity :additional_data="{ resource_type: 'Offer' }" :payload="offer"></link-entity>
          <a @click="sendCv" style="margin-right: 10px;" class="button is-info">Send CV</a>
          <a @click="vested" style="margin-right: 10px;" class="button is-info">Vested</a>
          <a @click="rejectOffer" style="margin-right: 10px;" class="button is-info">X</a>
          <a @click="openWhatsappTemplatesModal" style="margin-right: 10px; cursor: pointer;" title="שלח הודעת WhatsApp עם תבניות">
            <img src="/whatsapp.svg" width="30px" height="30px" style="filter: hue-rotate(120deg) saturate(2);">
          </a>
          <WhatsAppLink v-bind="offers_helper.getWhatsAppLinkPayload(offer, 'ghanaFlagMessage')" style="margin-right: 10px;"></WhatsAppLink>
          <button class="button mx-2" @click="sendWhatsapp">Send Offer WA</button>
        </div>
      </div>
      <div class="is-flex is-align-items-center">
        <offer-status :payload="offer"></offer-status>
        <div style="width: 10px;"></div>
        <p>עודכן ב- <b>{{ time_helper.makeTimeReadable(offer.updated_at) }}</b></p>
        <div style="width: 10px;"></div>
        <p>, מרחק - <b>{{ offer.distance }}</b></p>
      </div>
      <div>
        <div class="is-flex">
          <b>Offer Message: </b>
          <div style="width: 10px;"></div>
          <message :payload="offer" :additional_data="{ resource_type: 'Offer', endpoint: '/offers/', headers: 'admin_headers' }"></message>
        </div>
        <div class="is-flex">
          <AttributeDisplay :value="offer.applicant.preferred_work_region" :data="offer.applicant" title="Preferred Work Region" requestName="updateApplicant" requestValue="preferred_work_region" />
        </div>
        <div class="is-flex">
          <AttributeDisplay :value="offer.applicant.salary_expectations_comment" :data="offer.applicant" title="Salary Expectations" requestName="updateApplicant" requestValue="salary_expectations_comment" />
        </div>
        <p><b>Filters Message:</b> {{ offer.filters_message }}</p>
      </div>
    </div>
  `
}

export default {
  name: 'PositionRecommendationModal',
  /* eslint-disable vue/no-unused-components */
  components: {
    LinkEntity,
    OfferStatus,
    WhatsAppLink,
    Message,
    AttributeDisplay,
    OfferCard
  },
  /* eslint-enable vue/no-unused-components */
  props: ['payload'],
  data () {
    return {
      time_helper: new TimeHelper(),
      offers_helper: new OffersHelper(),
      offersMap: {},
      isLoadingOffers: false,
      copiedOfferId: null,
      steps: [
        { key: 'matching_titles', label: 'מתאים ג\'וב טייטלים...' },
        { key: 'filtering', label: 'מסנן משרות...' },
        { key: 'scoring', label: 'מחשב ניקוד...' },
        { key: 'scored', label: 'ניקוד הושלם!' }
      ]
    }
  },
  computed: {
    thisVm () {
      return this
    },
    applicantId () {
      return this.payload.data.applicant_id
    },
    session () {
      return this.$store.getters['positionRecommendations/getSession'](this.applicantId)
    },
    scoredItems () {
      return this.$store.getters['positionRecommendations/getScoredItems'](this.applicantId)
    },
    aiResults () {
      return this.$store.getters['positionRecommendations/getAiResults'](this.applicantId)
    },
    isActive () {
      return this.$store.getters['positionRecommendations/isActive'](this.applicantId)
    },
    matchedJobTitles () {
      return (this.session && this.session.matched_job_titles) || []
    },
    lastRole () {
      return (this.session && this.session.last_role) || ''
    },
    currentStepIndex () {
      if (!this.session) return 0
      const idx = this.steps.findIndex(s => s.key === this.session.status)
      return idx >= 0 ? idx : 0
    },
    errorMessage () {
      if (!this.session || !this.session.error) return 'שגיאה לא ידועה'
      const messages = {
        no_matching_job_titles: 'לא נמצאו ג\'וב טייטלים מתאימים למועמד',
        no_matching_positions: 'לא נמצאו משרות פעילות וחמות המתאימות',
        no_offers_found: 'לא נמצאו הצעות להערכה',
        applicant_missing_cv_data: 'למועמד אין קורות חיים או תקציר'
      }
      return messages[this.session.error] || this.session.error
    }
  },
  watch: {
    scoredItems (newItems) {
      if (newItems && newItems.length) {
        this.fetchFullOffers(newItems)
      }
    }
  },
  created () {
    if (!this.session || this.session.status === 'failed') {
      this.$store.dispatch('positionRecommendations/startRecommendation', {
        applicantId: this.applicantId,
        vm: this
      })
    } else if (this.isActive) {
      this.$store.dispatch('positionRecommendations/pollStatus', {
        applicantId: this.applicantId,
        sessionId: this.session.session_id,
        vm: this
      })
    } else {
      // Load data for scored sessions
      if (this.scoredItems && this.scoredItems.length) {
        this.fetchFullOffers(this.scoredItems)
      }
    }
  },
  beforeDestroy () {
    this.$store.dispatch('positionRecommendations/stopPolling', {
      applicantId: this.applicantId
    })
  },
  methods: {
    async fetchFullOffers (results) {
      const missingIds = results
        .map(r => r.offer_id)
        .filter(id => !this.offersMap[id])
      if (!missingIds.length) return

      this.isLoadingOffers = true
      for (const offerId of missingIds) {
        try {
          const offer = await this.$network_utils.getResourceOffers({ show_id: offerId })
          this.$set(this.offersMap, offerId, offer)
        } catch (e) {
          console.error('[PositionReco] Failed to load offer', offerId, e)
        }
      }
      this.isLoadingOffers = false
    },
    retry () {
      this.offersMap = {}
      this.$store.commit('positionRecommendations/clearSession', this.applicantId)
      this.$store.dispatch('positionRecommendations/startRecommendation', {
        applicantId: this.applicantId,
        vm: this
      })
    },
    evaluateOffer (offerId) {
      this.$store.dispatch('positionRecommendations/evaluateOffer', {
        applicantId: this.applicantId,
        offerId: offerId,
        vm: this
      })
    },
    isOfferLoading (offerId) {
      return this.$store.getters['positionRecommendations/isOfferAiLoading'](this.applicantId, offerId)
    },
    sendCv (offer) {
      this.create({
        designator: 'send-cv-mode-selection-modal',
        component_name: 'SendCvModeSelectionModal',
        data: offer,
        modal_title: 'בחירת מצב שליחת קורות חיים',
        modal_width: 600,
        is_center_modal: true
      })
    },
    vested (offer) {
      this.create({
        designator: 'vested-confirmation-modal',
        component_name: 'VestedConfirmationForm',
        data: offer,
        modal_title: 'Vested'
      })
    },
    rejectOffer (offer) {
      this.offers_helper.rejectOffer(offer)
    },
    openWhatsappTemplatesModal (offer) {
      this.create({
        designator: 'whatsapp-templates-modal',
        component_name: 'ModalOfferWhatsappTemplates',
        modal_title: 'הצעת משרה למועמד',
        modal_width: 900,
        is_center_modal: true,
        data: offer
      })
    },
    sendWhatsapp (offer) {
      this.create({
        designator: 'whatsapp-conversation-modal',
        component_name: 'ModalWhatsappMessager',
        modal_title: 'Conversation',
        props: {
          applicant: offer.applicant,
          offer: offer
        }
      })
    },
    copyOfferId (offerId) {
      navigator.clipboard.writeText(offerId.toString()).then(() => {
        this.copiedOfferId = offerId
        setTimeout(() => { this.copiedOfferId = null }, 2000)
      })
    },
    sanitize (text) {
      if (!text) return ''
      return text.replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/&lt;b&gt;/g, '<b>').replace(/&lt;\/b&gt;/g, '</b>')
        .replace(/&lt;i&gt;/g, '<i>').replace(/&lt;\/i&gt;/g, '</i>')
        .replace(/&lt;br\s*\/?&gt;/g, '<br>')
        .replace(/\n/g, '<br>')
    },
    decisionBadgeClass (decision) {
      return {
        'decision-green': decision === 'green',
        'decision-orange': decision === 'orange',
        'decision-red': decision === 'red' || decision === 'error'
      }
    },
    decisionLabel (decision) {
      const labels = {
        green: 'עובר',
        orange: 'לבדוק',
        red: 'לא מתאים',
        error: 'שגיאה'
      }
      return labels[decision] || decision || '-'
    },
    ...mapMutations('modals', ['create', 'remove'])
  }
}
</script>

<style scoped>
.position-recommendation-modal {
  padding: 1rem;
}

/* Steps */
.steps {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding: 0 0.5rem;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  opacity: 0.4;
  transition: opacity 0.3s;
}

.step.active { opacity: 1; }

.step.current .step-dot {
  background: #3273dc;
  color: white;
}

.step-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e0e0e0;
  font-size: 0.8rem;
  font-weight: 700;
}

.step.active .step-dot {
  background: #48c774;
  color: white;
}

.step-label {
  font-size: 0.75rem;
  color: #666;
  text-align: center;
  max-width: 90px;
}

/* Progress */
.progress-bar-container { margin-bottom: 1rem; }

.filtered-count {
  text-align: center;
  font-size: 0.95rem;
  color: #363636;
  margin-bottom: 0.4rem;
}

.progress-text {
  text-align: center;
  font-size: 0.85rem;
  color: #666;
  margin-top: 0.25rem;
}

.loading-spinner-container {
  display: flex;
  justify-content: center;
  padding: 1rem;
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 4px solid #dbdbdb;
  border-top-color: #3273dc;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* Error */
.error-section { text-align: center; padding: 2rem 1rem; }
.error-text { color: #f14668; margin-bottom: 1rem; font-size: 1rem; }

/* Matched job titles */
.matched-titles-block {
  background: #f5f0ff;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  border: 1px solid #d4c5f0;
  margin-bottom: 0.5rem;
}

.matched-titles-block h4 {
  margin: 0 0 0.5rem 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #363636;
}

.title-tags { display: flex; flex-wrap: wrap; gap: 0.4rem; }

.title-tag {
  display: inline-block;
  padding: 0.2rem 0.7rem;
  background: #7c5cbf;
  color: white;
  border-radius: 14px;
  font-size: 0.85rem;
  font-weight: 500;
}

/* Results */
.results-section { display: flex; flex-direction: column; gap: 0.75rem; }

.reasoning-block {
  background: #f0f7ff;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  border: 1px solid #b8d4f0;
}

.reasoning-block h4 {
  margin: 0 0 0.4rem 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #363636;
}

.reasoning-text { font-size: 0.9rem; color: #4a4a4a; line-height: 1.5; }

.results-summary {
  font-size: 0.9rem;
  color: #666;
  padding: 0.25rem 0;
  border-bottom: 1px solid #eee;
}

/* AI Recommendation banner per offer */
.ai-reco-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.6rem;
  background: #f5f0ff;
  border-radius: 4px;
  border: 1px solid #d4c5f0;
  margin-bottom: 0.4rem;
}

.ai-reco-rank {
  font-weight: 700;
  color: #3273dc;
  font-size: 1rem;
}

.ai-reco-badge {
  padding: 0.15rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  color: white;
}

.ai-reco-score {
  font-size: 0.85rem;
  font-weight: 600;
  color: #4a4a4a;
}

.ai-reco-position-link {
  margin-right: auto;
  font-weight: 600;
}

.ai-reco-position-text {
  margin-right: auto;
  font-weight: 600;
  color: #363636;
  font-size: 0.9rem;
}

.ai-reco-reasoning, .ai-reco-proposer {
  font-size: 0.85rem;
  color: #4a4a4a;
  line-height: 1.5;
  padding: 0.2rem 0.6rem;
  margin-bottom: 0.3rem;
}

.decision-green { background: #48c774; }
.decision-orange { background: #ff9f43; }
.decision-red { background: #f14668; }

/* Scored items section */
.scored-section {
  margin-top: 0.5rem;
}

.scored-divider {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  padding: 0.5rem 0;
  border-top: 3px solid #3273dc;
  text-align: center;
}

.scored-divider-label {
  display: inline-block;
  background: #3273dc;
  color: white;
  padding: 0.2rem 1rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

.scored-item-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.5rem;
  background: #f0f4ff;
  border-radius: 4px;
  border: 1px solid #c8d8f0;
  margin-bottom: 0.3rem;
}

.scored-item-rank {
  font-weight: 700;
  color: #3273dc;
  font-size: 0.9rem;
}

.scored-item-score {
  font-size: 0.85rem;
  font-weight: 600;
  color: #4a4a4a;
}

.scored-item-offer-id {
  font-size: 0.8rem;
  color: #888;
}

.scored-item-company {
  font-size: 0.85rem;
  font-weight: 600;
  color: #7c5cbf;
}

.scored-item-status {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.scored-item-position-link {
  margin-right: auto;
  font-weight: 600;
}

.scored-item-position-text {
  margin-right: auto;
  font-weight: 600;
  color: #363636;
  font-size: 0.9rem;
}

.no-results { text-align: center; padding: 1.5rem; color: #999; }

.offer-ai-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.6rem;
  color: #666;
  font-size: 0.85rem;
}

.loading-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  color: #666;
}

.horizontal_space { width: 10px; }
</style>
